import * as THREE from 'three';
import { WOLF_CLIPS, WOLF_GAITS, type WolfClip } from '../../../content/characters/rigs/wolf.js';
import { track } from './character-motion.js';
import type { WolfRig } from './wolf-model.js';

type Side = 'L' | 'R';
const SIDES: Side[] = ['L', 'R'];
const TAU = Math.PI * 2;
const smooth = THREE.MathUtils.smoothstep;
const wrap = (value: number) => THREE.MathUtils.euclideanModulo(value, 1);
/** A smooth 0 → 1 → 0 bump between two points of a clip; zero at both ends so loops close. */
const pulse = (u: number, from: number, to: number) => Math.sin(THREE.MathUtils.clamp((u - from) / (to - from), 0, 1) * Math.PI) ** 2;
const pawOffsets = new WeakMap<WolfRig, Map<string, THREE.Vector3[]>>();
// Shared scratch only; posing is synchronous and retains no rigs or per-frame resources.
const spineTarget = new THREE.Matrix4(), spineLocal = new THREE.Matrix4();
const spinePosition = new THREE.Vector3(), spineRotation = new THREE.Quaternion();

/** Cubic swing matches the backward stance velocity at lift-off and landing. */
export function wolfFootCycle(cycle: number, duty: number, stride: number, lift: number, front: boolean) {
  const u = wrap(cycle), reach = stride * duty / 2;
  if (u < duty) {
    const stance = u / duty;
    const pitch = track(stance, [[0, -.08], [.18, 0], [.68, 0], [1, .42]]);
    return { z: reach * (1 - 2 * stance), y: 0, pitch, planted: true };
  }
  const t = (u - duty) / (1 - duty), slope = -stride * (1 - duty);
  const z = (2*t**3 - 3*t**2 + 1) * -reach + (t**3 - 2*t**2 + t) * slope
    + (-2*t**3 + 3*t**2) * reach + (t**3 - t**2) * slope;
  const pitch = track(t, [[0, .42], [.38, front ? -.6 : .45], [.72, front ? -.3 : .18], [1, -.08]]);
  return { z, y: Math.sin(Math.PI*t)**1.5 * lift, pitch, planted: false };
}

/** Body-led poses with planted world-space paw targets, baked into editable FK keys. */
export function poseWolf(rig: WolfRig, clip: WolfClip, time: number) {
  rig.skeleton.pose();
  const joint = (name: string) => rig.joints.get(name)!;
  const turn = (name: string, x = 0, y = 0, z = 0) => joint(name).rotation.set(x, y, z);
  const u = wrap(time / WOLF_CLIPS[clip]), phase = u * TAU;
  const progress = THREE.MathUtils.clamp(time / WOLF_CLIPS[clip], 0, 1);
  const breath = Math.sin(phase * (clip === 'idle' ? 2 : 1)), breathWeight = clip === 'death' ? 1 - smooth(progress, 0, .2) : 1;
  const body = joint('body'), chest = joint('chest'), pelvis = joint('pelvis');
  const lumbar = joint('spine_lumbar'), thoracic = joint('spine_thoracic');
  const chestRestY = chest.position.y, chestRestZ = chest.position.z;
  const alignSpine = () => {
    // End poses remain authored in body space; the connected middle bends between the haunches and shoulders.
    spinePosition.set(chest.position.x, .04 + chest.position.y - chestRestY, .4 + chest.position.z - chestRestZ);
    spineTarget.compose(spinePosition, chest.quaternion, chest.scale);
    // Lumbar slope is authored relative to the body, independently of rear-leg loading.
    spineRotation.copy(pelvis.quaternion).invert().multiply(lumbar.quaternion);
    lumbar.quaternion.copy(spineRotation);
    body.updateWorldMatrix(true, true);
    spineTarget.premultiply(body.matrixWorld);
    spineLocal.copy(thoracic.matrixWorld).invert().multiply(spineTarget).decompose(chest.position, chest.quaternion, chest.scale);
  };

  chest.scale.set(1 + breath*.012*breathWeight, 1 + breath*.008*breathWeight, 1);
  chest.position.y += breath * .012 * breathWeight;
  turn('neck', .025 * Math.sin(phase + .3));
  turn('head', -.03 + .025 * Math.sin(phase + .7), .07 * Math.sin(phase));
  for (const side of SIDES) turn(`ear_${side}`, .06 * Math.sin(phase * 2 + (side === 'L' ? 0 : 1.7)));
  turn('tail_1', .08 * Math.sin(phase + .4), .16 * Math.sin(phase));
  turn('tail_2', .09 * Math.sin(phase), .14 * Math.sin(phase - .6));
  turn('tail_3', .1 * Math.sin(phase - .6), .12 * Math.sin(phase - 1.1));

  if (clip === 'idle') {
    // Six watchful seconds: slow breaths, the head sweeping across the scene, a sniff, each ear flicking on its own beat, and a brief tail wag.
    const sniff = pulse(u, .56, .72), wag = pulse(u, .82, .96);
    body.position.x += .023 * Math.sin(phase);
    body.position.y -= .025 + .009 * Math.cos(phase*2);
    turn('body', .012 * Math.sin(phase*2), 0, -.025 * Math.sin(phase));
    turn('pelvis', 0, .022 * Math.sin(phase + .4));
    turn('chest', .01 * Math.sin(phase + .9), -.03 * Math.sin(phase - .35));
    turn('neck', .025 + .04 * Math.sin(phase + .3) - .07 * sniff, .08 * Math.sin(phase - .35));
    turn('head', -.045 + .025 * Math.sin(phase*2) - .1 * sniff, .17 * Math.sin(phase - .35) + .06 * Math.sin(phase*2 + 1.1), .035 * Math.sin(phase - .35));
    turn('jaw', .035 * sniff * Math.abs(Math.sin(phase * 9)));
    // Ears swivel toward the look and flick back, out of step with one another.
    for (const [side, from] of [['L', .22], ['R', .64]] as const) {
      const flick = pulse(u, from, from + .08), sign = side === 'L' ? -1 : 1;
      turn(`ear_${side}`, .06 * Math.sin(phase * 2 + (side === 'L' ? 0 : 1.7)) - .32 * flick + .05 * sniff, .12 * Math.sin(phase - .35), sign * (.07 * Math.sin(phase + sign) + .42 * flick));
    }
    turn('tail_1', .08 * Math.sin(phase + .4), .16 * Math.sin(phase) + .22 * wag * Math.sin(phase * 8));
    turn('tail_2', .09 * Math.sin(phase), .14 * Math.sin(phase - .6) + .3 * wag * Math.sin(phase * 8 - .7));
    turn('tail_3', .1 * Math.sin(phase - .6), .12 * Math.sin(phase - 1.1) + .36 * wag * Math.sin(phase * 8 - 1.4));
    alignSpine();
    for (const side of SIDES) for (const front of [true, false]) plantPaw(rig, front, side);
  }

  if (clip === 'combat_idle') {
    // Low, staggered and loaded onto the forequarters; feet stay planted through the weight shift.
    body.position.y -= .175 + .024 * Math.cos(phase);
    body.position.z += .07 + .035 * Math.sin(phase);
    body.position.x += .026 * Math.sin(phase + .4);
    turn('body', .075 + .025 * Math.sin(phase), .025 * Math.sin(phase), -.04 * Math.sin(phase + .4));
    chest.position.y -= .025;
    pelvis.position.y += .035;
    turn('chest', .035 + .025 * Math.sin(phase + .6), -.028 * Math.sin(phase));
    turn('pelvis', -.06, .045 * Math.sin(phase + .6));
    turn('neck', .28 + .07 * Math.sin(phase + .8), .035 * Math.sin(phase));
    turn('head', -.23 - .055 * Math.sin(phase + .8), .06 * Math.sin(phase - .3));
    // A low growl: the jaw trembles while the lip stays drawn.
    turn('jaw', .10 + .10 * Math.sin(phase*.5)**4 + .028 * pulse(u, .3, .75) * Math.sin(phase * 14));
    alignSpine();
    for (const side of SIDES) {
      const sign = side === 'L' ? -1 : 1;
      turn(`ear_${side}`, -.35 + .075 * Math.sin(phase*2 + sign), 0, sign*.2);
      plantPaw(rig, true, side, new THREE.Vector3(sign*.35, .065, side === 'L' ? .69 : .55));
      plantPaw(rig, false, side, new THREE.Vector3(sign*.31, .065, side === 'L' ? -.62 : -.51));
    }
    turn('tail_1', .3 + .11 * Math.sin(phase), .15 * Math.sin(phase + .5));
    turn('tail_2', .13 + .13 * Math.sin(phase - .6), .13 * Math.sin(phase));
    turn('tail_3', .13 * Math.sin(phase - 1.1), .16 * Math.sin(phase - .8));
  }

  if (clip === 'walk' || clip === 'run') {
    const run = clip === 'run', gait = WOLF_GAITS[clip];
    // Rear and front paw sweeps drive their own back sections, delayed by the actual contact schedule.
    const sweep = (contact: number) => Math.sin((u - contact - gait.duty*.5)*TAU);
    const rearDrive = run ? sweep((gait.contacts.hind_R+gait.contacts.hind_L)*.5)
      : (sweep(gait.contacts.hind_R)-sweep(gait.contacts.hind_L))*.5;
    const frontDrive = run ? sweep((gait.contacts.front_R+gait.contacts.front_L)*.5)
      : (sweep(gait.contacts.front_R)-sweep(gait.contacts.front_L))*.5;
    if (run) {
      // Gallop: hind contacts, extended flight, fore contacts, then gathered flight.
      // A continuous two-flight arc carries speed through the cycle seam without a sharp drop off either crest.
      body.position.y += -.115 + .048*Math.cos((u-.445)*TAU*2);
      body.position.z += .008 * Math.sin(phase - .4);
      turn('body', -.045*Math.sin(phase-.16), 0, .008*Math.sin(phase));
      // Counter-bends travel through the weighted lumbar and thoracic sections.
      const lowerPitch = .12*rearDrive, upperPitch = .11*frontDrive;
      turn('spine_lumbar', lowerPitch);
      turn('spine_thoracic', upperPitch-lowerPitch);
      chest.position.z += .012*Math.cos((u-.4)*TAU-.6);
      pelvis.position.z -= .012*Math.cos((u-.4)*TAU+.6);
      const chestPitch = .04*Math.sin(phase-.7);
      turn('chest', chestPitch, .008*Math.sin(phase));
      turn('pelvis', .05*rearDrive, -.008*Math.sin(phase));
      turn('neck', .13 - body.rotation.x - chestPitch*.65 + .015*Math.sin(phase-.6));
      turn('head', -.13 - .012*Math.sin(phase-.6));
      turn('tail_1', .25 + .07*Math.sin(phase-.7), .025*Math.sin(phase));
      turn('tail_2', .055*Math.sin(phase-1.35), .025*Math.sin(phase-.4));
      turn('tail_3', .07*Math.sin(phase-1.9), .025*Math.sin(phase-.9));
    } else {
      body.position.y -= .085 + .0035*Math.cos(phase*2);
      body.position.x += .008*Math.sin(phase);
      body.position.z += .004*Math.sin(phase*2);
      turn('body', .008*Math.sin(phase*2), .007*Math.sin(phase), -.016*Math.sin(phase));
      // A sideways wave deforms the waist and mid-back before reaching the shoulders.
      const lowerYaw = .12*rearDrive, upperYaw = -.10*frontDrive;
      turn('spine_lumbar', 0, lowerYaw);
      turn('spine_thoracic', 0, upperYaw-lowerYaw);
      const chestYaw = -.018*frontDrive;
      turn('chest', -.008*Math.sin(phase*2+.5), chestYaw);
      turn('pelvis', .008*Math.sin(phase*2-.6), .036*rearDrive);
      // Carry the gaze ahead over a quiet back; ears stay attentive and the tail follows rather than wagging.
      const gazeYaw = -(body.rotation.y+chestYaw);
      const neckCounter = -(body.rotation.y*.64+chestYaw*.24)/.27;
      turn('neck', .12 - body.rotation.x - chest.rotation.x*.5 + .008*Math.sin(phase*2-.2), gazeYaw + .008*Math.sin(phase+.5) + neckCounter);
      turn('head', -.1 + .012*Math.sin(phase*2-.5), -.008*Math.sin(phase+.5) - neckCounter);
      for (const side of SIDES) turn(`ear_${side}`, .02*Math.sin(phase*2+(side === 'L' ? 0 : 1.4)), .025*Math.sin(phase+.6), (side === 'L' ? -1 : 1)*(.06 + .015*Math.sin(phase*2)));
      turn('tail_1', .10 + .016*Math.sin(phase*2), .045*Math.sin(phase+.3));
      turn('tail_2', .04 + .02*Math.sin(phase*2-.5), .055*Math.sin(phase-.2));
      turn('tail_3', .025*Math.sin(phase*2-1), .065*Math.sin(phase-.7));
    }
    alignSpine();
    for (const side of SIDES) for (const front of [true, false]) {
      const name = `${front ? 'front' : 'hind'}_${side}` as keyof typeof gait.contacts;
      const localCycle = wrap(u - gait.contacts[name]);
      const foot = wolfFootCycle(localCycle, gait.duty, gait.stride, run ? (front ? .30 : .34) : .115, front);
      const sign = side === 'L' ? -1 : 1;
      const shoulder = joint(`${front ? 'shoulder' : 'hip'}_${side}`);
      shoulder.position.z += (run ? (front ? .025 : .018) : (front ? .014 : .01))*Math.sin(localCycle*TAU);
      shoulder.position.y += (run ? .008 : .003)*Math.cos(localCycle*TAU);
      const gathered = Math.exp(-((wrap(u-.93+.5)-.5)**2)/.085**2);
      const extended = Math.exp(-((wrap(u-.445+.5)-.5)**2)/.065**2);
      const footY = foot.planted ? pawFloorHeight(rig,front,side,foot.pitch) : .065+foot.y+(run ? .10*gathered+.06*extended : 0);
      const target = new THREE.Vector3(sign*(run ? .245 : .25), footY, (front ? .57 : -.48) + foot.z);
      plantPaw(rig, front, side, target, foot.pitch,
        front ? foot.pitch*.45 : (run ? .34 : .14)*Math.sin(localCycle*TAU-.6));
      if (run) turn(`ear_${side}`, -.2 + .08*Math.sin(phase-.5), 0, sign*.08);
    }
  }

  if (clip === 'attack') {
    // Crouch onto the rear paws, drive late into the bite, then absorb it over the planted forepaws.
    const load = track(progress, [[0,0],[.28,1],[.42,.85],[.52,0],[.68,.35],[1,0]]);
    const lunge = track(progress, [[0,0],[.28,-.12],[.42,-.1],[.52,.34],[.58,.37],[.72,.29],[1,0]]);
    body.position.y += track(progress, [[0,-.14],[.28,-.23],[.42,-.20],[.52,-.10],[.61,-.19],[.73,-.17],[1,-.14]]);
    body.position.z += .06 + lunge;
    // Load the right haunch and catch the bite over the leading left forepaw, rather than squaring both shoulders.
    body.position.x += track(progress, [[0,0],[.28,.035],[.42,.025],[.55,-.04],[.7,-.025],[1,0]]);
    const twist = track(progress, [[0,0],[.28,.065],[.42,.025],[.52,-.05],[.65,-.08],[1,0]]);
    turn('body', track(progress, [[0,.04],[.23,-.08],[.52,.12],[.68,.08],[1,.04]]), twist, -.035*Math.sin(progress*TAU));
    turn('chest', -.10*load, -.045*load); turn('pelvis', .13*load, .025*load);
    chest.position.z += .045*(lunge/.4);
    const aim = -twist + .045*load;
    turn('neck', track(progress, [[0,.20],[.23,-.02],[.52,.32],[.68,.24],[1,.20]]), aim*.7);
    turn('head', track(progress, [[0,-.16],[.3,-.10],[.52,-.26],[.70,-.1],[1,-.16]]), aim*.3);
    turn('jaw', track(progress, [[0,.08],[.25,.56],[.42,.68],[.52,.02],[.64,.08],[.78,.20],[1,.08]]));
    alignSpine();
    for (const side of SIDES) {
      const sign = side === 'L' ? -1 : 1;
      const leading = side === 'L', liftAt = leading ? .32 : .38, landAt = leading ? .48 : .52;
      const recoverAt = leading ? .72 : .78, restAt = leading ? .94 : .99, reach = leading ? .32 : .23;
      const step = track(progress, [[0,0],[liftAt,0],[landAt,reach],[recoverAt,reach],[restAt,0],[1,0]]);
      const lift = pulse(progress, liftAt, landAt) * (leading ? .14 : .11) + pulse(progress, recoverAt, restAt) * .11;
      // Grounded targets use the paw's pad height; a lifted recovery is separate from the body settling.
      plantPaw(rig, true, side, new THREE.Vector3(sign*(leading ? .32 : .28), pawFloorHeight(rig, true, side, 0)+lift, (leading ? .64 : .56)+step));
      plantPaw(rig, false, side, new THREE.Vector3(sign*.28, pawFloorHeight(rig, false, side, 0), leading ? -.53 : -.48));
      turn(`ear_${side}`, -.4, 0, sign*.16);
    }
    turn('tail_1', .28 - .38*track(progress, [[0,0],[.3,1],[.6,-.6],[1,0]]));
    turn('tail_2', .26*track(progress, [[0,0],[.38,-1],[.70,1],[1,0]]));
    turn('tail_3', .30*track(progress, [[0,0],[.46,-1],[.78,.7],[1,0]]));
  }

  if (clip === 'flinch') {
    const hit = track(progress, [[0,0],[.13,1],[.36,-.22],[.58,.12],[1,0]]);
    body.position.z -= .16*hit;
    body.position.x -= .045*hit;
    body.position.y -= .09*Math.max(0,hit) + .07;
    turn('body', -.16*hit, .065*hit, -.13*hit);
    turn('chest', -.08*hit); turn('pelvis', .08*hit);
    turn('neck', -.32*hit); turn('head', -.23*hit, .20*hit, .08*hit);
    turn('jaw', .25*Math.max(0,hit));
    alignSpine();
    for (const side of SIDES) {
      const sign = side === 'L' ? -1 : 1;
      plantPaw(rig, true, side, new THREE.Vector3(sign*.30, .065, .57-(side==='R' ? .12 : .04)*Math.max(0,hit)));
      plantPaw(rig, false, side, new THREE.Vector3(sign*.28, .065, -.5));
      turn(`ear_${side}`, -.5*Math.max(0,hit), 0, sign*.30*Math.max(0,hit));
    }
    turn('tail_1', -.23*hit, .22*hit); turn('tail_2', -.3*hit, -.20*hit);
    turn('tail_3', -.3*track(progress, [[0,0],[.22,1],[.48,-.3],[1,0]]));
  }

  if (clip === 'death') {
    const hit = track(progress, [[0,0],[.08,1],[.19,.2],[.30,0],[1,0]]);
    body.position.y -= track(progress, [[0,0],[.12,.08],[.30,.27],[.53,.5],[.62,.44],[.76,.50],[1,.50]]);
    body.position.x -= track(progress, [[0,0],[.26,.03],[.52,.20],[.68,.24],[1,.24]]);
    body.position.z -= .08*hit;
    turn('body', -.10*hit, .04*hit, track(progress, [[0,0],[.18,.05],[.36,.48],[.53,1.64],[.62,1.46],[.74,1.6],[.90,Math.PI/2],[1,Math.PI/2]]));
    turn('chest', .06*hit, 0, track(progress, [[0,0],[.32,-.12],[.54,-.18],[.63,.10],[.78,-.035],[1,0]]));
    turn('pelvis', .13*hit, 0, track(progress, [[0,0],[.30,.13],[.50,.16],[.65,-.08],[1,0]]));
    turn('neck', track(progress, [[0,0],[.09,-.28],[.30,.32],[.53,.10],[.64,.52],[.77,.25],[1,.30]]));
    turn('head', track(progress, [[0,0],[.12,-.23],[.35,.14],[.56,-.14],[.68,.29],[.84,.17],[1,.18]]), .15*hit,
      track(progress, [[0,0],[.40,-.10],[.58,-.18],[.72,.07],[1,-.08]]));
    turn('jaw', track(progress, [[0,0],[.10,.20],[.35,.12],[.62,.36],[.82,.24],[1,.27]]));
    alignSpine();
    for (const side of SIDES) {
      const sign = side === 'L' ? -1 : 1;
      for (const front of [true,false]) {
        const lag = front ? (side==='L' ? .0 : .035) : (side==='L' ? -.035 : .015);
        const p = THREE.MathUtils.clamp(progress-lag,0,1);
        const settle = track(p, [[0,0],[.37,.35],[.54,1.12],[.65,.83],[.80,1.025],[.94,1],[1,1]]);
        const blend = smooth(p,.23,.49);
        if (blend < 1) plantPaw(rig, front, side, new THREE.Vector3(sign*(front ? .29 : .26), .065,
          front ? .57+(side==='L' ? .1 : 0)*smooth(progress,.12,.3) : -.48));
        const angles = front ? (side==='L' ? [-.55,1.10,-.32] : [.35,-.72,.18]) : (side==='L' ? [.5,-.95,.55] : [-.40,.72,-.40]);
        const names = front ? [`shoulder_${side}`,`elbow_${side}`,`wrist_${side}`] : [`hip_${side}`,`knee_${side}`,`hock_${side}`];
        names.forEach((name,index)=> {
          const angle = angles[index]*settle;
          const fold = side==='L' ? (front ? .28 : .32) : -.12;
          const rotation = new THREE.Quaternion().setFromEuler(new THREE.Euler(angle,0,index===0 ? fold*settle : 0));
          joint(name).quaternion.slerp(rotation,blend);
        });
        const paw = joint(`${front?'front':'hind'}_paw_${side}`);
        paw.quaternion.slerp(new THREE.Quaternion().setFromEuler(new THREE.Euler(front ? -.15 : .25,0,sign*.12)),blend);
      }
      turn(`ear_${side}`, -.25*smooth(progress,.12,.7), 0, sign*.22*smooth(progress,.2,.65));
    }
    turn('tail_1', track(progress, [[0,0],[.18,-.22],[.46,.32],[.60,-.23],[.76,.16],[1,.12]]));
    turn('tail_2', track(progress, [[0,0],[.3,-.32],[.55,.42],[.68,-.14],[.86,.2],[1,.18]]));
    turn('tail_3', track(progress, [[0,0],[.4,-.35],[.61,.38],[.75,-.18],[.9,.15],[1,.12]]));
  }

  rig.root.updateMatrixWorld(true);
  if (clip === 'death' && progress > .35) settleOnGround(rig, smooth(progress,.35,.63));
}

/** Toe targets are independent of the moving torso; metatarsals and paws have their own roll. */
function plantPaw(rig: WolfRig, front: boolean, side: Side, target?: THREE.Vector3, pitch = 0, anklePitch = 0) {
  const joint = (name:string) => rig.joints.get(name)!;
  const upper = joint(`${front?'shoulder':'hip'}_${side}`), lower = joint(`${front?'elbow':'knee'}_${side}`);
  const end = joint(`${front?'wrist':'hock'}_${side}`), paw = joint(`${front?'front':'hind'}_paw_${side}`);
  const sign = side==='L' ? -1 : 1;
  target ??= new THREE.Vector3(sign*.235,.065,front ? .57 : -.48);
  rig.root.updateMatrixWorld(true);
  const rootRotation = rig.root.getWorldQuaternion(new THREE.Quaternion());
  const endRotation = rootRotation.clone().multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(front?pitch*.45:anklePitch,0,0)));
  const offset = paw.position.clone();
  if (!front) offset.add(joint(`ankle_${side}`).position);
  const scale = rig.root.getWorldScale(new THREE.Vector3()).x;
  const desiredEnd = target.clone().applyMatrix4(rig.root.matrixWorld).sub(offset.applyQuaternion(endRotation).multiplyScalar(scale));
  solveWolfLimb(upper,lower,end,desiredEnd,front?-1:1,scale);
  end.quaternion.copy(end.parent!.getWorldQuaternion(new THREE.Quaternion()).invert().multiply(endRotation));
  if (!front) joint(`ankle_${side}`).quaternion.identity();
  rig.root.updateMatrixWorld(true);
  const pawRotation = rootRotation.multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(pitch,0,0)));
  paw.quaternion.copy(paw.parent!.getWorldQuaternion(new THREE.Quaternion()).invert().multiply(pawRotation));
  rig.root.updateMatrixWorld(true);
}

function solveWolfLimb(upper:THREE.Bone,lower:THREE.Bone,end:THREE.Bone,target:THREE.Vector3,bend:number,scale:number) {
  const start=upper.getWorldPosition(new THREE.Vector3()), direction=target.clone().sub(start);
  const a=lower.position.length()*scale, b=end.position.length()*scale;
  const distance=THREE.MathUtils.clamp(direction.length(),.03,a+b-.002);
  direction.normalize();
  const hint=new THREE.Vector3(0,0,bend).addScaledVector(direction,-direction.z*bend).normalize();
  const along=(a*a-b*b+distance*distance)/(2*distance);
  const middle=start.clone().addScaledVector(direction,along).addScaledVector(hint,Math.sqrt(Math.max(0,a*a-along*along)));
  const q1=new THREE.Quaternion().setFromUnitVectors(lower.position.clone().normalize(),middle.clone().sub(start).normalize());
  const q2=new THREE.Quaternion().setFromUnitVectors(end.position.clone().normalize(),start.clone().addScaledVector(direction,distance).sub(middle).normalize());
  upper.quaternion.copy(upper.parent!.getWorldQuaternion(new THREE.Quaternion()).invert().multiply(q1));
  lower.quaternion.copy(q1.clone().invert().multiply(q2));
  upper.updateWorldMatrix(true,true);
}

function settleOnGround(rig:WolfRig,amount:number) {
  rig.skeleton.update();
  let minimum=Infinity;
  const vertex=new THREE.Vector3();
  rig.root.traverse(object=>{if(object instanceof THREE.SkinnedMesh){const count=object.geometry.getAttribute('position').count;for(let i=0;i<count;i++){object.getVertexPosition(i,vertex).applyMatrix4(object.matrixWorld);minimum=Math.min(minimum,vertex.y);}}});
  rig.joints.get('body')!.position.y-=(minimum-.012)/rig.root.scale.y*amount;
  rig.root.updateMatrixWorld(true);
}

/** Heel/toe roll uses the actual paw volume, keeping its lowest pad on the ground. */
function pawFloorHeight(rig:WolfRig,front:boolean,side:Side,pitch:number) {
  let cached=pawOffsets.get(rig);
  if(!cached){cached=new Map();pawOffsets.set(rig,cached);}
  const name=`${front?'front':'hind'}_paw_${side}`;
  let points=cached.get(name);
  if(!points){
    points=[];
    const index=rig.skeleton.bones.indexOf(rig.joints.get(name)!);
    const centre=new THREE.Vector3().setFromMatrixPosition(rig.skeleton.boneInverses[index].clone().invert());
    rig.root.traverse(object=>{
      if(!(object instanceof THREE.SkinnedMesh))return;
      const positions=object.geometry.getAttribute('position'),ids=object.geometry.getAttribute('skinIndex'),weights=object.geometry.getAttribute('skinWeight');
      for(let i=0;i<positions.count;i++)if(ids.getX(i)===index&&weights.getX(i)>.999)points!.push(new THREE.Vector3().fromBufferAttribute(positions,i).sub(centre));
    });
    cached.set(name,points);
  }
  let minimum=Infinity;
  for(const point of points)minimum=Math.min(minimum,point.y*Math.cos(pitch)-point.z*Math.sin(pitch));
  return .005-minimum;
}

export function wolfAnimationClips(rig:WolfRig) {
  const clips:THREE.AnimationClip[]=[];
  for (const [name,duration] of Object.entries(WOLF_CLIPS) as [WolfClip,number][]) {
    const frames=Math.ceil(duration*60),times=Array.from({length:frames+1},(_,i)=>i*duration/frames);
    const data=[...rig.joints.values()].map(bone=>({bone,positions:[] as number[],rotations:[] as number[],scales:[] as number[]}));
    for(const time of times){poseWolf(rig,name,time);for(const item of data){item.positions.push(...item.bone.position.toArray());item.rotations.push(...item.bone.quaternion.toArray());item.scales.push(...item.bone.scale.toArray());}}
    const looping=['idle','combat_idle','walk','run'].includes(name);
    const tracks=data.flatMap(({bone,positions,rotations,scales})=>{
      if(looping){positions.splice(-3,3,...positions.slice(0,3));rotations.splice(-4,4,...rotations.slice(0,4));scales.splice(-3,3,...scales.slice(0,3));}
      return [new THREE.VectorKeyframeTrack(`${bone.name}.position`,times,positions),new THREE.QuaternionKeyframeTrack(`${bone.name}.quaternion`,times,rotations),new THREE.VectorKeyframeTrack(`${bone.name}.scale`,times,scales)];
    });
    clips.push(new THREE.AnimationClip(name,duration,tracks).optimize());
  }
  rig.skeleton.pose();return clips;
}
